import React, { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PodcastForm = ({ podcast, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    rss_feed: '',
    client_name: '',
    logo_url: '',
    status: 'active',
    episode_count: 0,
    category: 'General',
    ranking: '',
    monthly_downloads: ''
  });
  const [rssLoading, setRssLoading] = useState(false);
  const [useRss, setUseRss] = useState(true);

  useEffect(() => {
    if (podcast) {
      setFormData(podcast);
      setUseRss(!!podcast.rss_feed);
    }
  }, [podcast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'episode_count' ? parseInt(value) || 0 : value
    }));
  };

  const fetchFromRss = async () => {
    if (!formData.rss_feed) return;
    
    setRssLoading(true);
    try {
      const response = await axios.get(`${API}/podcasts/parse-rss`, {
        params: { rss_url: formData.rss_feed }
      });
      
      const data = response.data;
      setFormData(prev => ({
        ...prev,
        title: data.title || prev.title,
        description: data.description || prev.description,
        logo_url: data.cover_art || prev.logo_url,
        episode_count: data.episode_count || prev.episode_count,
        category: data.category || prev.category,
        author: data.author || prev.author
      }));
      
    } catch (error) {
      console.error('Error fetching RSS:', error);
      alert('Failed to fetch RSS feed. Please check the URL and try again.');
    } finally {
      setRssLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">
              {podcast ? 'Edit Podcast' : 'Add New Podcast'}
            </h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* RSS Toggle */}
            <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-lg">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={useRss}
                  onChange={(e) => setUseRss(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Use RSS Feed (Auto-fetch details)</span>
              </label>
            </div>

            {useRss && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RSS Feed URL</label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    name="rss_feed"
                    value={formData.rss_feed}
                    onChange={handleChange}
                    placeholder="https://feeds.example.com/podcast.xml"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={fetchFromRss}
                    disabled={rssLoading || !formData.rss_feed}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${rssLoading ? 'animate-spin' : ''}`} />
                    <span>{rssLoading ? 'Fetching...' : 'Fetch'}</span>
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Podcast Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image URL</label>
                <input
                  type="url"
                  name="logo_url"
                  value={formData.logo_url}
                  onChange={handleChange}
                  placeholder="https://example.com/cover.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Episode Count</label>
                <input
                  type="number"
                  name="episode_count"
                  value={formData.episode_count}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="e.g., Business, Health"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ranking</label>
                <input
                  type="text"
                  name="ranking"
                  value={formData.ranking}
                  onChange={handleChange}
                  placeholder="e.g., Top 10% in UK"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Downloads</label>
                <input
                  type="text"
                  name="monthly_downloads"
                  value={formData.monthly_downloads}
                  onChange={handleChange}
                  placeholder="e.g., 50K+"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (podcast ? 'Update Podcast' : 'Add Podcast')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PodcastForm;